import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CDatePicker,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSwitch,
  CLoadingButton,
  CTimePicker,
} from '@coreui/react-pro'

import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import RoundService from 'src/service/RoundService'
import ToastComponent from 'src/components/common/TaostComponent'

const EditForm = ({ roundId, visible, onClose, fetchUsers }) => {
  const [roundDetail, setRoundDetail] = useState()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [lockoutStartTime, setLockoutStartTime] = useState('')
  const [lockoutEndTime, setLockoutEndTime] = useState('')
  const [errorMessages, setErrorMessages] = useState([])
  const [loader, setLoader] = useState(false)

  useEffect(() => {
    if (visible && roundId) {
      RoundService.getRoundDetail(roundId)
        .then((res) => {
          if (res.status === 200) {
            if (res.data.start_date !== '0000-00-00 00:00:00') {
              setStartDate(moment(res.data.start_date).format('YYYY-MM-DD'))
              setLockoutStartTime(moment(res.data.start_date).format('h:mm A'))
            }
            if (res.data.end_date !== '0000-00-00 00:00:00') {
              setEndDate(moment(res.data.end_date).format('YYYY-MM-DD'))
              setLockoutEndTime(moment(res.data.end_date).format('h:mm A'))
            }
            setRoundDetail(res.data)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
    }
  }, [visible, roundId])

  const validationSchema = Yup.object().shape({
    round: Yup.string().required('Round is required'),
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date()
      .required('End date is required')
      .min(Yup.ref('start_date'), 'End date must be later than start date'),
    start_time: Yup.string().required('Start time is required'),
    end_time: Yup.string()
      .required('End time is required')
      .test('is-not-same', 'End time cannot be the same as start time', function (value) {
        const { start_time, start_date, end_date } = this.parent
        if (moment(start_date).isSame(end_date, 'day')) {
          return value !== start_time
        }
        return true
      })
      .test('is-later', 'End time must be later than start time', function (value) {
        const { start_time, start_date, end_date } = this.parent
        if (moment(start_date).isSame(end_date, 'day')) {
          const startTimeParsed = moment(start_time, 'h:mm A')
          const endTimeParsed = moment(value, 'h:mm A')
          return endTimeParsed.isAfter(startTimeParsed)
        }
        return true
      }),
  })

  const formik = useFormik({
    initialValues: {
      round: roundDetail?.round,
      start_date: roundDetail?.start_date,
      end_date: roundDetail?.end_date,
      start_time: lockoutStartTime,
      end_time: lockoutEndTime,
      trade_restriction: roundDetail?.trade_restriction,
      is_free_trades: roundDetail?.is_free_trades,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      const payload = {
        round_id: roundId,
        round: data.round,
        start_date: startDate,
        end_date: endDate,
        start_time: moment(lockoutStartTime, 'h:mm A').format('HH:mm'),
        end_time: moment(lockoutEndTime, 'h:mm A').format('HH:mm'),
        trade_restriction: data.trade_restriction,
        is_free_trades: data.is_free_trades,
      }

      setLoader(true)
      RoundService.editRound(payload)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers()
            ToastComponent(res.message, 'success')
            onClose()
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const handleClose = () => {
    setErrorMessages([])
    onClose()
  }

  const handleStartDate = (event) => {
    const dateFormat = moment(event, 'DD/MM/YYYY').format('YYYY-MM-DD')
    setStartDate(dateFormat)
    formik.setFieldValue('start_date', dateFormat)
    formik.validateField('end_date')
  }

  const handleEndDate = (event) => {
    const dateFormat = moment(event, 'DD/MM/YYYY').format('YYYY-MM-DD')
    setEndDate(dateFormat)
    formik.setFieldValue('end_date', dateFormat)
  }

  const handleLockoutStartTime = (event) => {
    const timeFormat = moment(event, ['h:mm A', 'HH:mm'])
    if (!timeFormat.isValid()) return
    const formatted = timeFormat.format('h:mm A')
    setLockoutStartTime(formatted)
    formik.setFieldValue('start_time', formatted)
  }

  const handleLockoutEndTime = (event) => {
    const timeFormat = moment(event, ['h:mm A', 'HH:mm'])
    if (!timeFormat.isValid()) return
    const formatted = timeFormat.format('h:mm A')
    setLockoutEndTime(formatted)
    formik.setFieldValue('end_time', formatted)
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 480 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Round {roundDetail?.round}</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update the lockout window and trade settings.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel htmlFor="round">Round *</CFormLabel>
            <CFormInput
              className={
                'form-control' + (formik.errors.round && formik.touched.round ? ' is-invalid' : '')
              }
              id="round"
              placeholder="Round"
              defaultValue={roundDetail?.round}
              onChange={formik.handleChange}
              disabled
            />
            {formik.errors.round && formik.touched.round && (
              <CFormFeedback invalid>{formik.errors.round}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="start_date">Round Start Date *</CFormLabel>
            <CDatePicker
              date={startDate}
              locale="en-US"
              placeholder="Start Date"
              onDateChange={handleStartDate}
              format="dd/MM/yyyy"
              cleaner={false}
            />
            {formik.errors.start_date && formik.touched.start_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="start_time">Round Start Time *</CFormLabel>
            <CTimePicker
              locale="en-US"
              cleaner={false}
              time={lockoutStartTime}
              value={lockoutStartTime}
              seconds={false}
              className={formik.errors.start_time && formik.touched.start_time ? 'is-invalid' : ''}
              onTimeChange={(e) => {
                handleLockoutStartTime(e)
                formik.setTouched({ ...formik.touched, start_time: true })
              }}
            />
            {formik.errors.start_time && formik.touched.start_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="end_date">Round End Date *</CFormLabel>
            <CDatePicker
              date={endDate}
              locale="en-US"
              placeholder="End Date"
              onDateChange={handleEndDate}
              format="dd/MM/yyyy"
              cleaner={false}
            />
            {formik.errors.end_date && formik.touched.end_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="end_time">Round End Time *</CFormLabel>
            <CTimePicker
              locale="en-US"
              cleaner={false}
              time={lockoutEndTime}
              value={lockoutEndTime}
              seconds={false}
              className={formik.errors.end_time && formik.touched.end_time ? 'is-invalid' : ''}
              onTimeChange={(e) => {
                handleLockoutEndTime(e)
                formik.setTouched({ ...formik.touched, end_time: true })
              }}
            />
            {formik.errors.end_time && formik.touched.end_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
          </CCol>
          <CCol xs={6}>
            <CFormSwitch
              id="trade_restriction"
              label={`Restrict Trade — ${roundDetail?.trade_restriction ? 'On' : 'Off'}`}
              checked={!!formik.values.trade_restriction}
              onChange={(e) => {
                setRoundDetail((prev) => ({ ...prev, trade_restriction: e.target.checked }))
                formik.setFieldValue('trade_restriction', e.target.checked)
                formik.setTouched({ ...formik.touched, trade_restriction: true })
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormSwitch
              id="is_free_trades"
              label={`Free Trade — ${roundDetail?.is_free_trades ? 'On' : 'Off'}`}
              checked={!!formik.values.is_free_trades}
              onChange={(e) => {
                setRoundDetail((prev) => ({ ...prev, is_free_trades: e.target.checked }))
                formik.setFieldValue('is_free_trades', e.target.checked)
                formik.setTouched({ ...formik.touched, is_free_trades: true })
              }}
            />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default EditForm
